import { Header, notify } from "@general-backoffice/core/index";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Col, Container, Row } from "reactstrap";

import { referrerEndpoints } from "../../../../api";
import DefaultReferralsTableCard from "../../../../components/referral/DefaultReferralsTableCard";
import useReferrals from "../../../../components/referral/hooks/useReferrals";
import { ClaimFlowModal } from "../../../../components/referrer/claim";
import InfoModal from "../../../../components/referrer/default/InfoModal";
import RewardsTableCard from "../../../../components/referrer/default/RewardsTableCard";
import DefaultTnCModal from "../../../../components/referrer/default/TnCModal";
import DiscountWalletCard from "../../../../components/referrer/DiscountWalletCard";
import AmbassadorRequirementsCard from "../../../../components/referrer/partner/AmbassadorRequirementsCard";
import PartnerTnCModal from "../../../../components/referrer/partner/TnCModal";
import ReferralLinkCard from "../../../../components/referrer/ReferralLinkCard";
import generateReferrerLink from "../../../../components/referrer/utils/generateReferrerLink";
import WarningBanner from "../../../../components/warnings/WarningBanner";
import useFetch from "../../../../hooks/useFetch";
import type { RootState } from "../../../../redux/reducers/session/state";
import { notifyLoadingToError, notifyLoadingToSuccess } from "../../../../utils/notify/notifyLoadingUpdate";
import refreshMe from "../../../../utils/refreshMe";

const ReferrerDefault: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard", { keyPrefix: "referrerDefault" });
  const state = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [showModalAcceptPartnerTnC, setShowModalAcceptPartnerTnC] = useState<boolean>(false);

  // Check if admin is impersonating this user
  const isImpersonating = useSelector((state: RootState) => !!state.sessionState?.impersonatedUserId);
  const [showClaimFlow, setShowClaimFlow] = useState(false);

  const tncAccepted = state?.sessionState?.user?.hasAcceptedAffiliateTnC;
  const userBlocked = state?.sessionState?.user?.isBlockedFromAffiliate;

  const {
    state: discountWallet,
    isLoading: isLoadingDiscountWallet,
    refresh: requestDiscountWallet,
  } = useFetch(() => referrerEndpoints.getDiscountWallet().then(({ data }) => data));

  const {
    state: rewards,
    isLoading: isLoadingRewards,
    refresh: requestRewards,
  } = useFetch(() => referrerEndpoints.getRewards().then(({ data }) => data));

  const { state: requirementsToAmbassador, isLoading: isLoadingRequirementsToAmbassador } = useFetch(() =>
    referrerEndpoints.getRequirementsTo("AMBASSADOR").then(({ data }) => data)
  );

  const {
    pagination: { total: totalReferrals },
  } = useReferrals({
    pagination: { pageSize: 1 },
    mine: true,
  });

  const {
    referrals = [],
    isLoading: isLoadingReferrals,
    refresh: requestReferrals,
    ...referralsProps
  } = useReferrals({
    pagination: { pageSize: 5 },
    filters: { expired: false, claimed: false },
    mine: true,
  });

  const [isOpenGeneralInfo, setIsOpenGeneralInfo] = useState(false);
  const referralsLength = referrals?.length ?? 0;
  // Open info modal on entering if has no referrals
  useEffect(() => {
    if (!isLoadingReferrals && !referralsLength) setIsOpenGeneralInfo(true);
  }, [isLoadingReferrals, referralsLength]);

  const {
    state: referrerCode,
    isLoading: isLoadingReferrerCode,
    refresh: requestReferrerCode,
  } = useFetch(() => referrerEndpoints.getReferrerCode().then(({ data }) => data), { auto: tncAccepted });
  const referrerLink = referrerCode ? generateReferrerLink(referrerCode) : undefined;

  return (
    <>
      <DefaultTnCModal
        isOpen={!tncAccepted}
        close={() => navigate("/")}
        acceptTerms={() => {
          referrerEndpoints
            .acceptTnC()
            .then(() => refreshMe({ state, dispatch }))
            .catch(() => notify.error(t("errorOnAcceptTnC")));
        }}
      />

      <PartnerTnCModal
        isOpen={showModalAcceptPartnerTnC}
        close={() => setShowModalAcceptPartnerTnC(false)}
        acceptTerms={() => {
          const notification = notify.loading(t("convertingToAmbassador"));
          referrerEndpoints
            .becomeAmbassador()
            .then(() => {
              notifyLoadingToSuccess(notification, t("convertedToAmbassador"));
              window.document.location.reload();
            })
            .catch(() => notifyLoadingToError(notification, t("convertedToAmbassadorError")));
        }}
      />

      <InfoModal isOpen={!!tncAccepted && isOpenGeneralInfo} close={() => setIsOpenGeneralInfo(false)} />

      {/* New Tax Compliance Claim Flow - Only visible when impersonating */}
      {isImpersonating && (
        <ClaimFlowModal
          isOpen={showClaimFlow}
          onClose={() => setShowClaimFlow(false)}
          onSuccess={() => {
            setShowClaimFlow(false);
            requestDiscountWallet();
            requestRewards();
          }}
        />
      )}

      <Header.BreadCrumb
        color="secondary"
        isDark={false}
        buttons={[
          {
            children: <i className="fas fa-info-circle"></i>,
            color: "default",
            size: "md",
            className: "shadow-none position-absolute",
            style: { transform: "translateY(-50%)" },
            onClick: () => setIsOpenGeneralInfo(true),
          },
        ]}
      />
      <Container className="mt--6" fluid>
        <Row>
          <Col xs={12} lg={6}>
            {userBlocked && (
              <div style={{ marginBottom: 30 }}>
                <WarningBanner
                  title={t("userBlockedWarning.title")}
                  text={
                    <Trans
                      t={t}
                      i18nKey={"content"}
                      components={[
                        <a key="0" href="mailto:customer@investhinks.com">
                          customer@investhinks.com
                        </a>,
                      ]}
                    />
                  }
                  type={"danger"}
                />
              </div>
            )}
            <DiscountWalletCard
              amount={discountWallet?.balance ?? 0}
              unused={discountWallet?.availableRewards?.reduce((sum, r) => sum + (r.amount || 0), 0) ?? 0}
              isLoading={isLoadingDiscountWallet}
            />
            <RewardsTableCard
              rewards={rewards?.discountRewards}
              isLoading={isLoadingRewards}
              onClaim={async () => {
                referrerEndpoints.claimDiscountReward().then(() => {
                  notify.success(t("messageSuccessClaimed"));
                  requestRewards();
                  requestReferrerCode();
                  requestDiscountWallet();
                  requestReferrals();
                });
              }}
              numToShow={10}
            />
          </Col>
          <Col xs={12} lg={6}>
            <ReferralLinkCard
              link={referrerLink}
              isLoading={isLoadingReferrerCode}
              title={t("referralLinkCard.title")}
              subtitle={t("referralLinkCard.subtitle")}
              onClickButton={async () => {
                // TODO: Fix type - referrerLink should always be string when button is clickable
                await navigator.clipboard.writeText(referrerLink ?? "");
                notify(t("referralLinkCard.messageLinkCopied"));
              }}
            />
            <DefaultReferralsTableCard
              {...referralsProps}
              totalReferrals={totalReferrals}
              isLoading={isLoadingReferrals}
              referrals={referrals}
            />
            <AmbassadorRequirementsCard
              requirements={requirementsToAmbassador?.requirements}
              isLoading={isLoadingRequirementsToAmbassador}
              onSubmit={() => setShowModalAcceptPartnerTnC(true)}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ReferrerDefault;
